import React from "react";

import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import { faPencil } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { get, isEmpty } from "lodash";
import { useSelector } from "react-redux";

import { FaIcon } from "components/AgGridButtonIcon/agGridButtonIcon";
import { Tooltip } from "components/Form/FormikControls/Tooltip";

import { SaleType } from "constants/sale";

import { XeroIntegrationToolTip } from "lib/agGrid/tooltips";
import {
  openEditBusinessModal,
  openEditIntegrationBusinessModal,
} from "lib/navigation";

import { getCurrentSale } from "selectors";

import XeroLogo from "img/Xero";

import { ClickableColumn, IconClickableColumn } from "./columnStyles";

export function BusinessRenderer({
  value,
  data,
  businessKey,
  complainIfMissing = false,
  node,
}) {
  const saleType = useSelector(getCurrentSale)?.sale_type;
  const readOnly = saleType === SaleType.REFERENCE_SALE;

  const businessName = value;
  let businessId = null;

  if (node.group) {
    if (node.allLeafChildren.length > 0) {
      businessId = get(node.allLeafChildren[0].data, businessKey);
    }
    // Grouping by another column, or what is now an empty group just return the value, no need to make it clickable - its probably blank.
    if (!businessId) {
      return value;
    }
  }
  if (data) {
    businessId = get(data, businessKey);
  }

  const onClickEdit = businessId
    ? () => {
        openEditBusinessModal(businessId);
      }
    : null;

  if (readOnly) {
    return businessName;
  }

  if (typeof businessName === "string" && businessName !== "" && businessId) {
    return (
      <ClickableColumn onClick={onClickEdit}>
        {businessName}
        <FontAwesomeIcon icon={faPencil} />
      </ClickableColumn>
    );
  } else if (businessId && complainIfMissing) {
    return (
      <FaIcon
        icon={faExclamationCircle}
        color="warning"
        onClick={onClickEdit}
        style={onClickEdit ? { cursor: "pointer" } : {}}
      />
    );
  } else {
    return null;
  }
}

export function SyRefBusinessRenderer({ value, data, businessKey }) {
  if (!data) {
    return "";
  }
  const businessId = get(data, businessKey);
  const onClickEdit = businessId
    ? () => {
        openEditBusinessModal(businessId);
      }
    : null;

  if (!value) {
    return (
      <FaIcon
        icon={faExclamationCircle}
        color="warning"
        onClick={onClickEdit}
        style={onClickEdit ? { cursor: "pointer" } : {}}
      />
    );
  }
  return (
    <ClickableColumn onClick={onClickEdit}>
      {value}
      <FontAwesomeIcon icon={faPencil} />
    </ClickableColumn>
  );
}

export function IntegrationBusinessRenderer({
  businessKeys,
  colDef,
  complainIfMissing = true,
  data,
  integrationType,
  useLogoAndTooltip,
}) {
  if (!data) {
    return null;
  }

  let business = null;
  if (businessKeys) {
    business =
      businessKeys
        .map(businessKey =>
          businessKey === null ? data : get(data, businessKey),
        )
        .filter(business => business && !isEmpty(business))[0] || {};
  } else {
    business = data;
  }

  const integrationBusiness = get(data, colDef.field) || {};

  const hasBusiness = Boolean(business.id);

  const onClickEdit = hasBusiness
    ? () => {
        openEditIntegrationBusinessModal(integrationType, business.id);
      }
    : null;

  const { name } = integrationBusiness;

  const tooltipComponent = (
    <XeroIntegrationToolTip integrationBusiness={integrationBusiness} />
  );

  if (typeof name === "string" && name !== "") {
    if (useLogoAndTooltip) {
      return (
        <Tooltip title={tooltipComponent}>
          <IconClickableColumn onClick={onClickEdit}>
            <XeroLogo />
          </IconClickableColumn>
        </Tooltip>
      );
    } else {
      return (
        <ClickableColumn onClick={onClickEdit}>
          {name}
          <FontAwesomeIcon icon={faPencil} />
        </ClickableColumn>
      );
    }
  } else if (hasBusiness && complainIfMissing) {
    return (
      <IconClickableColumn onClick={onClickEdit}>
        <FaIcon icon={faExclamationCircle} color="warning" />
      </IconClickableColumn>
    );
  } else {
    return null;
  }
}
